import React from 'react';
import {
  BarChart3,
  Globe,
  Instagram,
  Link2,
  MapPin,
  MessageCircle,
  Share2,
  TrendingUp
} from 'lucide-react';

export function MarketingHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-violet-500/[0.08] via-transparent to-sky-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* Instagram profile */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,260px)] rounded-2xl border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(168,85,247,0.25)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2 mb-3">
          <Instagram className="w-4 h-4 text-fuchsia-400" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{mock.instagramLabel}</span>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-purple-600/20 border border-fuchsia-500/25" />
          <div className="flex-1 space-y-1.5">
            <div className="h-2 w-20 rounded-full bg-white/20" />
            <div className="h-1.5 w-28 rounded-full bg-white/10" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="aspect-square rounded-md bg-white/[0.06] border border-white/[0.05]" />
          ))}
        </div>
      </div>

      {/* Google Meu Negócio */}
      <div className="absolute right-0 top-0 w-[min(100%,240px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(56,189,248,0.3)] p-3 z-10 rotate-[2deg] sm:right-2 lg:right-6 hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-1.5 mb-2.5 px-1">
          <MapPin className="w-3.5 h-3.5 text-sky-400" aria-hidden />
          <span className="text-[10px] font-semibold text-white/70">{mock.googleLabel}</span>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-sky-500/10 to-slate-900/80 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 text-sky-300" aria-hidden />
            <div className="h-2 flex-1 rounded-full bg-white/15" />
          </div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="w-2 h-2 rounded-sm bg-amber-400/60" />
            ))}
          </div>
          <div className="h-6 rounded-md bg-emerald-500/20 border border-emerald-500/25" />
        </div>
      </div>

      {/* WhatsApp Business */}
      <div className="absolute left-8 sm:left-16 bottom-32 sm:bottom-36 w-[130px] rounded-[1.25rem] border border-white/[0.15] bg-slate-950/90 backdrop-blur-xl shadow-[0_16px_50px_-16px_rgba(0,0,0,0.8)] p-2 z-30 rotate-[4deg] hover:rotate-0 transition-transform duration-500">
        <div className="rounded-xl bg-gradient-to-b from-emerald-500/15 to-slate-900/90 p-2 border border-white/[0.06]">
          <MessageCircle className="w-3.5 h-3.5 text-emerald-400 mx-auto mb-1.5" aria-hidden />
          <span className="block text-[8px] text-center font-semibold text-white/60 mb-2">{mock.whatsappLabel}</span>
          <div className="space-y-1">
            <div className="h-5 rounded-lg bg-emerald-500/15 border border-emerald-500/20 px-1.5 flex items-center">
              <div className="h-1 w-full rounded-full bg-white/15" />
            </div>
            <div className="h-5 rounded-lg bg-white/[0.04] border border-white/[0.05]" />
          </div>
        </div>
      </div>

      {/* Linktree */}
      <div className="absolute right-8 sm:right-14 bottom-20 sm:bottom-24 w-[min(100%,180px)] rounded-xl border border-violet-500/25 bg-violet-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="w-4 h-4 text-violet-400 shrink-0" aria-hidden />
          <span className="text-[10px] font-bold text-violet-200/95">{mock.linktreeLabel}</span>
        </div>
        <div className="space-y-1">
          <div className="h-4 rounded-md bg-violet-500/20 border border-violet-500/25" />
          <div className="h-4 rounded-md bg-white/[0.06] border border-white/[0.05]" />
          <div className="h-4 rounded-md bg-white/[0.04] border border-white/[0.04]" />
        </div>
      </div>

      {/* Social insights */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(99,102,241,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-200/80">{mock.insightsLabel}</span>
          <Share2 className="w-3.5 h-3.5 text-indigo-400" aria-hidden />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[65, 48, 82].map((h, i) => (
            <div key={i} className="rounded-lg bg-gradient-to-t from-indigo-500/25 to-indigo-400/10 border border-indigo-500/15 flex items-end p-1.5" style={{ height: `${h}px` }}>
              <div className="w-full h-1.5 rounded-full bg-indigo-400/40" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-white/[0.08]">
          <BarChart3 className="w-3.5 h-3.5 text-indigo-400" aria-hidden />
          <span className="text-[10px] text-indigo-300 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" aria-hidden />
            {mock.metric} · {mock.live}
          </span>
        </div>
      </div>
    </div>
  );
}
